import { useEffect, useId, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/shared/components/icon'
import { Skeleton } from '@/shared/components/ui'
import { Label } from '@/shared/components/ui/label'
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from '@/shared/components/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select'

type PaginacaoProps = {
  totalItens: number
  onMudarPagina: (pagina: number, itensPorPagina: number) => void
  paginaInicial?: number
  itensPorPaginaInicial?: number
  isLoading?: boolean
  opcoesItensPorPagina?: number[]
}

export default function Paginacao({
  totalItens,
  onMudarPagina,
  isLoading,
  opcoesItensPorPagina = [20, 30, 50],
  paginaInicial = 1,
  itensPorPaginaInicial = 20,
}: Readonly<PaginacaoProps>) {
  const id = useId()
  const { t } = useTranslation('common')
  const [paginaCorrente, setPaginaCorrente] = useState(paginaInicial)
  const [itensPorPagina, setItensPorPagina] = useState(itensPorPaginaInicial)

  const totalDePaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina))
  const handleMudarItensPorPagina = (valor: string) => {
    const novosItensPorPagina = Number(valor)
    setItensPorPagina(novosItensPorPagina)
    setPaginaCorrente(1)
    onMudarPagina(1, novosItensPorPagina)
  }
  const handleMudarPagina = (pagina: number) => {
    if (pagina >= 1 && pagina <= totalDePaginas) {
      setPaginaCorrente(pagina)
      onMudarPagina(pagina, itensPorPagina)
    }
  }

  useEffect(() => {
    setPaginaCorrente(paginaInicial)
  }, [paginaInicial])

  return (
    <div className="gp:4 flex flex-wrap sm:items-center sm:justify-between sm:gap-8" data-testid="paginacao-component">
      <div className="flex min-w-full items-center gap-3 sm:min-w-0">
        <Label className="text-gray-600" htmlFor={id}>
          {t('paginacao.exibir')}
        </Label>
        <Select defaultValue="20" value={String(itensPorPagina)} onValueChange={handleMudarItensPorPagina}>
          <SelectTrigger id={id} className="w-fit bg-white whitespace-nowrap">
            <SelectValue placeholder="Select number of results" />
          </SelectTrigger>
          <SelectContent className="[&_*[role=option]]:ps-2 [&_*[role=option]]:pe-8 [&_*[role=option]>span]:start-auto [&_*[role=option]>span]:end-2">
            {opcoesItensPorPagina?.map((opcao) => (
              <SelectItem key={opcao} value={opcao.toString()}>
                {opcao}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Label className="text-gray-600">{t('paginacao.itens')}</Label>
      </div>
      <div className="text-muted-foreground flex grow text-sm whitespace-nowrap sm:justify-end" aria-live="polite">
        <span className="flex items-center justify-center gap-1 text-gray-600">
          {paginaCorrente} {t('paginacao.de')} {isLoading ? <Skeleton className="h-4 w-6" /> : totalDePaginas}{' '}
          {t('paginacao.paginas')}
        </span>
      </div>
      <div>
        <Pagination>
          <PaginationContent data-testid="pagination-component">
            <PaginationItem>
              <PaginationLink
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                onClick={() => handleMudarPagina(1)}
                aria-label="Go to first page"
                aria-disabled={paginaCorrente === 1}
              >
                <Icon name="chevron-first" size={16} aria-hidden="true" />
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                onClick={() => handleMudarPagina(paginaCorrente - 1)}
                aria-label="Go to previous page"
                aria-disabled={paginaCorrente === 1}
              >
                <Icon name="chevron-left" size={16} aria-hidden="true" />
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                onClick={() => handleMudarPagina(paginaCorrente + 1)}
                aria-label="Go to next page"
                aria-disabled={paginaCorrente === totalDePaginas}
              >
                <Icon name="chevron-right" size={16} aria-hidden="true" />
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                onClick={() => handleMudarPagina(totalDePaginas)}
                aria-label="Go to last page"
                aria-disabled={paginaCorrente === totalDePaginas}
              >
                <Icon name="chevron-last" size={16} aria-hidden="true" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  )
}
